import { useState, useEffect } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { fetchProductsFromSheet } from "../utils/sheetProducts";
import { productCustomiseImageUrls } from "../utils/parseProductMedia";
import { ProductImageWithFallbacks } from "../components/ProductImageWithFallbacks";
import { ProductPriceBlock } from "../components/ProductPriceBlock";
import GiftBoxLidEditor, {
  normalizeGiftLidLayout,
  normalizeGiftLidTextColor,
} from "../components/GiftBoxLidEditor";
import "../styles/gift-customise.css";
import { BtnContent } from "../components/BtnIcon";
import {
  IoArrowBackOutline,
  IoCartOutline,
  IoCheckmarkCircle,
  IoStorefrontOutline,
} from "react-icons/io5";

const MESSAGE_MAX = 200;

const DEFAULT_LAYOUT = { position: "center", scale: 1 };

/**
 * Customise flow: message on a real product photo “lid”, with preset
 * positions and resizable text; layout is stored with the cart line.
 */
function GiftCustomise({ addToCart, updateCartItem }) {
  const { productId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const editCartLine = Boolean(location.state?.editCartLine);
  const returnTo = location.state?.returnTo || "/cart";

  const [product, setProduct] = useState(location.state?.product || null);
  const [loadError, setLoadError] = useState(null);
  const [message, setMessage] = useState(location.state?.giftLidMessage || "");
  const [lidLayout, setLidLayout] = useState(
    location.state?.giftLidLayout
      ? normalizeGiftLidLayout(location.state.giftLidLayout)
      : DEFAULT_LAYOUT,
  );
  const [lidTextColor, setLidTextColor] = useState(
    normalizeGiftLidTextColor(location.state?.giftLidTextColor),
  );
  const [added, setAdded] = useState(false);

  useEffect(() => {
    const fromNav = location.state?.product;
    if (fromNav && productId && String(fromNav.id) === String(productId)) {
      setProduct(fromNav);
      setLoadError(null);
    }
  }, [location.state, productId]);

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchProductsFromSheet();
        if (cancelled) return;
        const found = list.find((p) => String(p.id) === String(productId));
        if (found) setProduct(found);
        else setLoadError("That product was not found.");
      } catch {
        if (!cancelled) setLoadError("Could not load products. Try again later.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [productId]);

  useEffect(() => {
    if (editCartLine) {
      setMessage(location.state?.giftLidMessage || "");
      setLidLayout(
        location.state?.giftLidLayout
          ? normalizeGiftLidLayout(location.state.giftLidLayout)
          : DEFAULT_LAYOUT,
      );
      setLidTextColor(normalizeGiftLidTextColor(location.state?.giftLidTextColor));
      return;
    }
    setLidLayout(DEFAULT_LAYOUT);
    setLidTextColor("gold");
    setMessage("");
  }, [product?.id, editCartLine, location.state]);

  const handleAdd = () => {
    if (!product || product.stock === 0) return;
    const customisation = {
      giftLidMessage: message.trim().slice(0, MESSAGE_MAX) || undefined,
      giftLidLayout: normalizeGiftLidLayout(lidLayout),
      giftLidTextColor: normalizeGiftLidTextColor(lidTextColor),
    };

    if (editCartLine && updateCartItem) {
      updateCartItem(product.id, customisation);
      navigate(returnTo);
      return;
    }

    addToCart(product, customisation);
    setAdded(true);
    window.setTimeout(() => {
      setAdded(false);
      navigate("/cart");
    }, 600);
  };

  if (loadError) {
    return (
      <div className="gift-customise-page">
        <p className="text-muted mb-3">{loadError}</p>
        <Link to="/shop" className="btn btn-primary">
          <BtnContent icon={IoStorefrontOutline}>Browse shop</BtnContent>
        </Link>
      </div>
    );
  }

  if (productId && !product) {
    return (
      <div className="gift-customise-page">
        <p className="text-muted">Loading…</p>
      </div>
    );
  }

  if (!productId && !product) {
    return (
      <div className="gift-customise-page">
        <nav className="gift-customise-page__crumb">
          <Link to="/shop">← Shop</Link>
        </nav>
        <h1>Customise your gift box</h1>
        <p className="gift-customise-page__sub">
          Add a short message on the gift box — we print what you type. Choose a gift box from the shop to use your
          product photo on the preview.
        </p>
        <p className="gift-customise-hint-row">
          Choose a position, colour, and text size below — your message updates on the preview above.
        </p>
        <GiftBoxLidEditor
          imageUrls={[]}
          message={message}
          onMessageChange={setMessage}
          maxLength={MESSAGE_MAX}
          layout={lidLayout}
          onLayoutChange={setLidLayout}
          textColor={lidTextColor}
          onTextColorChange={setLidTextColor}
          editorId="gift-lid-msg-generic"
          countId="gift-lid-count-generic"
        />
        <div className="gift-customise-empty">
          <p>Select a gift box first so we can attach your message to the right item.</p>
          <Link to="/shop" className="btn btn-primary">
            <BtnContent icon={IoStorefrontOutline}>Choose a gift box</BtnContent>
          </Link>
        </div>
      </div>
    );
  }

  const previewImageUrls = productCustomiseImageUrls(product);

  return (
    <div className="gift-customise-page">
      <nav className="gift-customise-page__crumb">
        <Link to="/shop">Shop</Link>
        <span className="text-muted mx-2">·</span>
        <Link to={`/product/${product.id}`} state={{ product }}>
          {product.name}
        </Link>
      </nav>

      <h1>Customise your gift box</h1>
      <p className="gift-customise-page__sub">
        Your message appears on this product photo on the gift box. Pick a position, colour, and size, then add to cart.
      </p>
      <p className="gift-customise-hint-row">
        Use the position buttons for top left, top right, center, bottom left, or bottom right.
      </p>

      <div className="gift-customise-product-pill">
        <div className="gift-customise-product-pill__img">
          <ProductImageWithFallbacks urls={previewImageUrls} alt="" loading="lazy" />
        </div>
        <div className="gift-customise-product-pill__meta">
          <h2>{product.name}</h2>
          <p>
            {product.boxSizeLabel || product.fabric} · {product.color}
          </p>
          <ProductPriceBlock product={product} variant="shop" />
        </div>
      </div>

      <GiftBoxLidEditor
        imageUrls={previewImageUrls}
        message={message}
        onMessageChange={setMessage}
        maxLength={MESSAGE_MAX}
        layout={lidLayout}
        onLayoutChange={setLidLayout}
        textColor={lidTextColor}
        onTextColorChange={setLidTextColor}
        editorId="gift-lid-msg"
        countId="gift-lid-count"
      />

      <div className="gift-customise-actions">
        <button
          type="button"
          className="btn btn-dark px-4"
          onClick={handleAdd}
          disabled={product.stock === 0 || added}
        >
          {product.stock === 0 ? (
            "Out of stock"
          ) : editCartLine ? (
            added ? (
              <BtnContent icon={IoCheckmarkCircle}>Updated</BtnContent>
            ) : (
              <BtnContent icon={IoCheckmarkCircle}>Save message to cart</BtnContent>
            )
          ) : added ? (
            <BtnContent icon={IoCheckmarkCircle}>Added — opening cart…</BtnContent>
          ) : (
            <BtnContent icon={IoCartOutline}>Add to cart with this message</BtnContent>
          )}
        </button>
        <Link to={`/product/${product.id}`} state={{ product }} className="btn btn-outline-secondary">
          <BtnContent icon={IoArrowBackOutline}>Back to product</BtnContent>
        </Link>
      </div>
    </div>
  );
}

export default GiftCustomise;
